import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../layout/Layout";
import LoadingOverlay from "react-loading-overlay-ts";
import Api from "../Config/ApiConfig";
import { toast } from "react-toastify";
import moment from "moment";

const STATUS_OPTIONS = ["Pending", "InProcess", "Resolved", "Invalid", "Complete"];

const formatChatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = moment(dateStr);
  const now = moment();
  if (d.isSame(now, "day")) {
    return "Today";
  }
  if (d.isSame(now.clone().subtract(1, "day"), "day")) {
    return "Yesterday";
  }
  if (d.isSame(now, "year")) {
    return d.format("D MMMM");
  }
  return d.format("D MMMM YYYY");
};

const isAiMissingInfo = (msg) => {
  if (!msg) return false;
  if (msg.response_type === "unsupported" || msg.response_type === "unrelated") return true;
  if (msg.ai_prompt === "SKIPPED_LLM_LOW_RELEVANCE") return true;
  const reply = (msg.ai_response || "").toLowerCase();
  if (
    reply.includes("don't have enough information") ||
    reply.includes("not enough information") ||
    reply.includes("contact buddy4travel support") ||
    reply.includes("help with buddy4travel-related questions only")
  ) {
    return true;
  }
  return false;
};

const SourceBadge = ({ source }) => {
  switch (source) {
    case "partner_app":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-800 border border-purple-200">
          <i className="fas fa-handshake text-[10px]"></i>
          <span>Partner App</span>
        </span>
      );
    case "customer_app":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-sky-100 text-sky-800 border border-sky-200">
          <i className="fas fa-mobile-alt text-[10px]"></i>
          <span>Customer App</span>
        </span>
      );
    case "web":
    default:
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
          <i className="fas fa-globe text-[10px]"></i>
          <span>Web</span>
        </span>
      );
  }
};

export default function ChatSupportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [history, setHistory] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [updatingMsgId, setUpdatingMsgId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalMessages, setTotalMessages] = useState(0);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [initialScrolled, setInitialScrolled] = useState(false);
  const [adminReplyText, setAdminReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const chatContainerRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await Api.get(`chat-support/details/${id}?page=1&limit=20`);
      if (res.data?.success) {
        setUser(res.data.data.user);
        setHistory(res.data.data.history || []);
        setPage(1);
        setHasMore(Boolean(res.data.data.pagination?.hasMore));
        setTotalMessages(res.data.data.pagination?.total ?? (res.data.data.history?.length || 0));
        setInitialScrolled(false);
      }
    } catch (error) {
      console.error("Fetch Detail Error:", error);
      toast.error("Failed to load details.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Scroll to bottom when history first loads
  useEffect(() => {
    if (history.length > 0 && !initialScrolled && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      setInitialScrolled(true);
    }
  }, [history, initialScrolled]);

  const handleLoadOlder = async () => {
    if (loadingOlder || !hasMore) return;

    const container = chatContainerRef.current;
    const previousScrollHeight = container ? container.scrollHeight : 0;
    const previousScrollTop = container ? container.scrollTop : 0;

    const nextPage = page + 1;
    try {
      setLoadingOlder(true);
      const res = await Api.get(`chat-support/details/${id}?page=${nextPage}&limit=20`);
      if (res.data?.success) {
        const olderBatch = res.data.data.history || [];
        setHistory((prev) => [...olderBatch, ...prev]);
        setPage(nextPage);
        setHasMore(Boolean(res.data.data.pagination?.hasMore));
        setTotalMessages(res.data.data.pagination?.total ?? (totalMessages));

        // Maintain scroll position after prepending older messages
        requestAnimationFrame(() => {
          if (chatContainerRef.current) {
            const newScrollHeight = chatContainerRef.current.scrollHeight;
            chatContainerRef.current.scrollTop = newScrollHeight - previousScrollHeight + previousScrollTop;
          }
        });
      }
    } catch (error) {
      console.error("Load older messages error:", error);
      toast.error("Failed to load older messages.");
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (updating) return;
    try {
      setUpdating(true);
      const res = await Api.put(`chat-support/status/${id}`, { status: newStatus });
      if (res.data?.success) {
        toast.success(`Session status updated to ${newStatus}`);
        setUser(res.data.data);
      }
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to update status";
      toast.error(msg);
    } finally {
      setUpdating(false);
    }
  };

  const handleMessageStatusUpdate = async (messageId, newStatus) => {
    if (!messageId || updatingMsgId) return;
    try {
      setUpdatingMsgId(messageId);
      const res = await Api.put(`chat-support/message-status/${messageId}`, { status: newStatus });
      if (res.data?.success) {
        toast.success(`Query marked as ${newStatus}`);
        setHistory((prev) =>
          prev.map((m) => (m._id === messageId ? { ...m, status: newStatus } : m))
        );
      }
    } catch (error) {
      console.error("Message status update error:", error);
      toast.error(error.response?.data?.message || "Failed to update query status");
    } finally {
      setUpdatingMsgId(null);
    }
  };

  const handleSendAdminReply = async (e) => {
    if (e) e.preventDefault();
    if (!adminReplyText.trim() || sendingReply) return;

    try {
      setSendingReply(true);
      const res = await Api.post(`/chat-support/reply/${id}`, {
        message: adminReplyText.trim()
      });

      if (res.data?.success) {
        toast.success("Reply sent to customer successfully");
        const newHistoryItem = {
          _id: `reply_${Date.now()}`,
          user_message: "[Admin Direct Reply]",
          ai_response: adminReplyText.trim(),
          createdAt: new Date().toISOString(),
          metadata: { is_admin_reply: true },
          status: "InProcess"
        };
        setHistory((prev) => [...prev, newHistoryItem]);
        setAdminReplyText("");
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        }, 100);
      }
    } catch (error) {
      console.error("Admin reply error:", error);
      toast.error(error.response?.data?.message || "Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <Layout>
      <LoadingOverlay active={loading} text="Loading...">
        <div className="dashboard_data_wrap">
          <div className="dashbord_cont_wrapper">
            {/* Back Button */}
            <button
              onClick={() => navigate("/chat-support")}
              className="flex items-center text-emerald-600 hover:text-emerald-800 font-semibold mb-6 transition-colors cursor-pointer"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to List
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Sidebar: User Details & Status */}
              <div className="lg:col-span-1 space-y-6">
                {/* User Card */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">User Details</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between pb-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500 font-medium">Platform</span>
                      <SourceBadge source={user?.source} />
                    </div>
                    <DetailItem label="Name" value={user?.name} />
                    <DetailItem label="Email" value={user?.email} />
                    <DetailItem label="Mobile" value={user?.mobile || "N/A"} />
                    {user?.agent_id && (
                      <DetailItem label="Agent ID" value={user.agent_id} isCode />
                    )}
                    {user?.audience && user?.audience !== "public" && (
                      <DetailItem label="Audience Scope" value={user.audience} />
                    )}
                    <DetailItem label="Session ID" value={user?.session_id} isCode />
                    <DetailItem label="Created At" value={user ? moment(user.createdAt).format("DD MMM YYYY, HH:mm") : ""} />
                    {user?.feedback && (
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-100 rounded-lg">
                        <p className="text-[10px] font-bold text-yellow-600 uppercase tracking-wider mb-1">User Feedback</p>
                        <p className="text-sm text-gray-800 italic">"{user.feedback}"</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Card */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Ticket Status</h3>
                  <div className="mb-4">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Active Status</span>
                    <span className="inline-flex items-center px-3.5 py-1 rounded-full text-sm font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      {user?.status || "Pending"}
                    </span>
                  </div>

                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2.5">Change Status</h4>
                  <div className="space-y-1.5">
                    {STATUS_OPTIONS.map((status) => {
                      const isCurrent = user?.status === status || (status === "Resolved" && user?.status === "Solved");
                      return (
                        <button
                          key={status}
                          type="button"
                          onClick={() => handleStatusUpdate(status)}
                          disabled={updating || isCurrent}
                          className={`w-full text-left px-3.5 py-2 rounded-lg border text-xs font-semibold transition-all flex items-center justify-between group ${
                            isCurrent
                              ? "bg-emerald-50 border-emerald-400 text-emerald-800 font-bold"
                              : "border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 text-gray-700 cursor-pointer"
                          } disabled:cursor-default`}
                        >
                          <span>{status}</span>
                          {isCurrent ? (
                            <span className="text-[11px] font-bold text-emerald-700 flex items-center gap-1">
                              <i className="fas fa-check"></i> Current
                            </span>
                          ) : (
                            <i className="fas fa-arrow-right text-[10px] text-gray-300 group-hover:text-emerald-500 transition-colors"></i>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Chat History */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col h-full overflow-hidden min-h-[650px] max-h-[800px]">
                  {/* WhatsApp-Style Header */}
                  <div className="bg-gray-50 px-6 py-3.5 border-b border-gray-200 flex justify-between items-center flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-700 text-white flex items-center justify-center font-bold text-sm shadow-xs">
                        {user?.name ? user.name[0].toUpperCase() : <i className="fas fa-user"></i>}
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-gray-900 m-0 leading-tight">{user?.name || "Chat Support"}</h3>
                        <span className="text-xs text-gray-500 font-mono">Session: {user?.session_id}</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      Showing <strong className="text-emerald-700">{history.length}</strong> of <strong className="text-gray-700">{totalMessages}</strong> messages
                    </div>
                  </div>

                  {/* Scrollable Chat Area */}
                  <div
                    ref={chatContainerRef}
                    className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4"
                    style={{
                      backgroundColor: "#f0f2f5",
                      backgroundImage: "radial-gradient(#d1d5db 0.75px, transparent 0.75px)",
                      backgroundSize: "24px 24px"
                    }}
                  >
                    {/* Load 20 Older Messages Button at Top */}
                    {hasMore && (
                      <div className="flex justify-center my-2">
                        <button
                          type="button"
                          disabled={loadingOlder}
                          onClick={handleLoadOlder}
                          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-500 shadow-xs transition-all cursor-pointer group disabled:opacity-50"
                        >
                          {loadingOlder ? (
                            <>
                              <span className="spinner-border spinner-border-sm text-emerald-600" role="status" style={{ width: "0.85rem", height: "0.85rem" }}></span>
                              <span>Loading 20 older messages...</span>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-arrow-up text-emerald-500 group-hover:-translate-y-0.5 transition-transform"></i>
                              <span>Load 20 Older Messages ({totalMessages - history.length} remaining)</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {!hasMore && totalMessages > 20 && (
                      <div className="flex justify-center my-2">
                        <span className="text-[11px] text-gray-400 font-medium px-3 py-1 bg-white/80 rounded-full border border-gray-200 shadow-2xs">
                          Beginning of chat history ({totalMessages} messages total)
                        </span>
                      </div>
                    )}

                    {history.length > 0 ? (
                      history.map((msg, idx) => {
                        const currentDate = moment(msg.createdAt).format("YYYY-MM-DD");
                        const prevMsg = idx > 0 ? history[idx - 1] : null;
                        const prevDate = prevMsg ? moment(prevMsg.createdAt).format("YYYY-MM-DD") : null;
                        const showDateHeader = currentDate !== prevDate;
                        const isMissingInfo = isAiMissingInfo(msg);

                        return (
                          <React.Fragment key={msg._id || idx}>
                            {/* WhatsApp Date Separator Badge */}
                            {showDateHeader && (
                              <div className="flex justify-center my-4 sticky top-1 z-10 pointer-events-none">
                                <span className="px-3.5 py-1 text-[11px] font-bold text-gray-600 bg-white/95 backdrop-blur-sm border border-gray-200/90 rounded-full shadow-xs uppercase tracking-wider">
                                  {formatChatDate(msg.createdAt)}
                                </span>
                              </div>
                            )}

                            {/* User Message (Sent - Right Aligned) */}
                            <div className="flex flex-col items-end">
                              <div className="max-w-[85%] sm:max-w-[75%] bg-emerald-700 text-white rounded-2xl rounded-tr-xs px-4 py-2.5 shadow-sm">
                                <p className="text-sm m-0 leading-relaxed whitespace-pre-wrap select-text">{msg.user_message}</p>
                                <div className="flex items-center justify-end gap-1.5 mt-1 text-[10px] text-emerald-200/80">
                                  <span>{moment(msg.createdAt).format("hh:mm A")}</span>
                                  <i className="fas fa-check-double text-[9px]"></i>
                                </div>
                              </div>
                            </div>

                            {/* AI Assistant Response (Received - Left Aligned) */}
                            <div className="flex flex-col items-start">
                              {isMissingInfo ? (
                                /* Highlighted Missing Info Bubble */
                                <div className="max-w-[92%] sm:max-w-[82%] bg-amber-50/95 border-2 border-amber-400 text-gray-900 rounded-2xl rounded-tl-xs shadow-md overflow-hidden">
                                  {/* Warning Banner */}
                                  <div className="bg-amber-100/90 px-4 py-2 border-b border-amber-200 flex items-center justify-between gap-2 flex-wrap">
                                    <div className="flex items-center gap-2 text-xs font-bold text-amber-900">
                                      <i className="fas fa-exclamation-triangle text-amber-600"></i>
                                      <span>AI Lacked Information</span>
                                    </div>
                                    <span
                                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                                        msg.status === "Resolved" || msg.status === "Solved"
                                          ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                                          : msg.status === "Invalid"
                                          ? "bg-gray-200 text-gray-700 border-gray-300"
                                          : "bg-amber-200 text-amber-900 border-amber-300"
                                      }`}
                                    >
                                      Status: {msg.status || "Pending"}
                                    </span>
                                  </div>

                                  <div className="p-4">
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                      <span className="w-2 h-2 rounded-full bg-amber-500 inline-block"></span>
                                      <span className="text-xs font-bold text-amber-800">AI Assistant (Fallback)</span>
                                    </div>
                                    <p className="text-sm m-0 leading-relaxed whitespace-pre-wrap select-text text-amber-950 font-normal">
                                      {msg.ai_response}
                                    </p>

                                    {/* Admin Action Controls on this Question */}
                                    <div className="mt-3 pt-2.5 border-t border-amber-200 flex items-center justify-between flex-wrap gap-2">
                                      <span className="text-[11px] font-semibold text-amber-900">
                                        Admin Action on this query:
                                      </span>
                                      <div className="flex items-center gap-1.5">
                                        <button
                                          type="button"
                                          disabled={updatingMsgId === msg._id}
                                          onClick={() => handleMessageStatusUpdate(msg._id, "Resolved")}
                                          className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all cursor-pointer ${
                                            msg.status === "Resolved" || msg.status === "Solved"
                                              ? "bg-emerald-600 text-white shadow-xs"
                                              : "bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-50"
                                          }`}
                                        >
                                          <i className="fas fa-check mr-1"></i>
                                          Resolved
                                        </button>

                                        <button
                                          type="button"
                                          disabled={updatingMsgId === msg._id}
                                          onClick={() => handleMessageStatusUpdate(msg._id, "Invalid")}
                                          className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all cursor-pointer ${
                                            msg.status === "Invalid"
                                              ? "bg-gray-700 text-white shadow-xs"
                                              : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                                          }`}
                                        >
                                          <i className="fas fa-ban mr-1"></i>
                                          Invalid
                                        </button>

                                        {(msg.status === "Resolved" || msg.status === "Solved" || msg.status === "Invalid") && (
                                          <button
                                            type="button"
                                            disabled={updatingMsgId === msg._id}
                                            onClick={() => handleMessageStatusUpdate(msg._id, "Pending")}
                                            className="px-2 py-1 rounded-md text-[11px] font-medium text-gray-500 hover:text-gray-800 bg-white border border-gray-200 hover:bg-gray-50 cursor-pointer"
                                            title="Reset back to Pending"
                                          >
                                            <i className="fas fa-undo mr-1"></i>
                                            Reset
                                          </button>
                                        )}
                                      </div>
                                    </div>

                                    <div className="flex items-center justify-end mt-2 text-[10px] text-amber-700/70">
                                      <span>{moment(msg.createdAt).format("hh:mm A")}</span>
                                    </div>
                                  </div>
                                </div>
                              ) : msg.user_message === "[Admin Direct Reply]" || msg.metadata?.is_admin_reply ? (
                                /* Admin Direct Reply Bubble */
                                <div className="max-w-[85%] sm:max-w-[75%] bg-blue-50 border border-blue-200 text-blue-900 rounded-2xl rounded-tl-xs px-4 py-2.5 shadow-sm">
                                  <div className="flex items-center gap-1.5 mb-1.5 border-b border-blue-100 pb-1">
                                    <span className="w-2 h-2 rounded-full bg-blue-600 inline-block"></span>
                                    <span className="text-xs font-bold text-blue-700">Support Team (Admin Reply)</span>
                                  </div>
                                  <p className="text-sm m-0 leading-relaxed whitespace-pre-wrap select-text text-blue-950 font-medium">{msg.ai_response}</p>
                                  <div className="flex items-center justify-end mt-1.5 text-[10px] text-blue-400">
                                    <span>{moment(msg.createdAt).format("hh:mm A")}</span>
                                  </div>
                                </div>
                              ) : (
                                /* Standard AI Assistant Bubble */
                                <div className="max-w-[85%] sm:max-w-[75%] bg-white border border-gray-200/90 text-gray-800 rounded-2xl rounded-tl-xs px-4 py-2.5 shadow-sm">
                                  <div className="flex items-center gap-1.5 mb-1.5 border-b border-gray-100 pb-1">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
                                    <span className="text-xs font-bold text-emerald-700">AI Assistant</span>
                                  </div>
                                  <p className="text-sm m-0 leading-relaxed whitespace-pre-wrap select-text text-gray-800">{msg.ai_response}</p>
                                  <div className="flex items-center justify-end mt-1.5 text-[10px] text-gray-400">
                                    <span>{moment(msg.createdAt).format("hh:mm A")}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-gray-400 py-20">
                        <i className="fas fa-ghost text-4xl mb-3"></i>
                        <p>No chat history found for this session.</p>
                      </div>
                    )}
                  </div>

                  {/* WhatsApp-Style Reply Bar */}
                  <form
                    onSubmit={handleSendAdminReply}
                    className="p-3 bg-white border-t border-gray-200 flex items-center gap-2"
                  >
                    <input
                      type="text"
                      className="flex-1 px-4 py-2 text-sm bg-gray-50 border border-gray-300 rounded-full focus:outline-none focus:border-emerald-500 focus:bg-white transition-all text-gray-800"
                      placeholder="Type a reply to customer..."
                      value={adminReplyText}
                      onChange={(e) => setAdminReplyText(e.target.value)}
                      disabled={sendingReply}
                    />
                    <button
                      type="submit"
                      disabled={sendingReply || !adminReplyText.trim()}
                      className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-full shadow-xs transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sendingReply ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" style={{ width: "0.85rem", height: "0.85rem" }}></span>
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <span>Send Reply</span>
                          <i className="fas fa-paper-plane text-xs"></i>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </LoadingOverlay>
    </Layout>
  );
}

function DetailItem({ label, value, isCode }) {
  return (
    <div>
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
      <p className={`text-sm font-medium text-gray-900 m-0 ${isCode ? 'font-mono text-xs bg-gray-50 px-2 py-0.5 rounded' : ''}`}>
        {value}
      </p>
    </div>
  );
}
